import React, { createContext, useState, useContext, useMemo, useEffect, useCallback } from "react";
import useUser from "../../hooks/useuser";
import { IAssessment, LicensedEnum, sessionStorageKeys } from "../types";
import buildCatPlayerUrl from "../diagnostics/buildDiagnosticLaunchUrl";
import buildScreenerUrl from "../screeners/buildScreenerLaunchUrl";
import useAssessmentData from "./useAssessmentData";
import { createOneTimeToken } from "../../helpers/auth-service";

interface IAssessmentProviderProps {
  children: React.ReactNode;
}

interface IAssessmentContext {
  assessments: IAssessment[] | undefined;
  grade: number | undefined;
  subject: string;
  subjects: string[];
  loading: boolean;
  setSubject: (subject: string) => void;
  licensed: LicensedEnum;
  launchDiagnostic: (testNumber: number, replace: boolean) => void;
  launchScreener: (testNumber: number, isRetake: boolean) => void;
  reloadData: () => void;
}

const AssessmentContext = createContext<IAssessmentContext>({
  assessments: [],
  grade: 0,
  subject: "",
  subjects: [],
  loading: false,
  setSubject: () => {
    // default implementation
  },
  licensed: LicensedEnum.UNKNOWN,
  launchDiagnostic: () => {
    // default implementation
  },
  launchScreener: () => {
    // default implementation
  },
  reloadData: () => {
    // default implementation
  }
});
export function AssessmentProvider(props: IAssessmentProviderProps) {
  const { children } = props;
  const [subjects] = useState(["ELA", "Math"]);
  const [subject, setSubject] = useState<string>("All");
  const [studentGrade, setStudentGrade] = useState<number | undefined>();
  const [licensed, setLicensed] = useState<LicensedEnum>(LicensedEnum.UNKNOWN);

  const { user } = useUser();
  const { loading, assessments, reloadData } = useAssessmentData(user, licensed, subject);

  useEffect(() => {
    if (user) {
      setStudentGrade(user.gradeLevel);
      const isLicensed =
        user.products &&
        user.products.includes("IPA") &&
        user.organization?.licensedProducts.some((product) => product.includes("IPA"));
      setLicensed(isLicensed ? LicensedEnum.LICENSED : LicensedEnum.UNLICENSED);
    }
  }, [user]);

  const launchDiagnostic = useCallback(
    (testNumber: number, replace = false) => {
      const diagnosticType = sessionStorage.getItem(sessionStorageKeys.diagnosticSubject);
      if (diagnosticType && diagnosticType !== "none") {
        // build the launch url
        const catLaunchUrl = buildCatPlayerUrl(diagnosticType, user, testNumber);
        if (sessionStorage.getItem(sessionStorageKeys.oneTimeToken)) {
          createOneTimeToken().then((token) => {
            if (replace) {
              window.location.replace(`${catLaunchUrl}&ott=${token}`);
            } else {
              window.location.assign(`${catLaunchUrl}&ott=${token}`);
            }
          });
        } else if (replace) {
          window.location.replace(catLaunchUrl);
        } else {
          window.location.assign(catLaunchUrl);
        }
      }
    },
    [user]
  );

  const launchScreener = useCallback(
    (testNumber: number, isRetake: boolean) => {
      const launchUrl = buildScreenerUrl(testNumber, user, isRetake);
      if (sessionStorage.getItem(sessionStorageKeys.oneTimeToken)) {
        createOneTimeToken().then((token) => {
          window.location.assign(`${launchUrl}&ott=${token}`);
        });
      } else {
        window.location.assign(launchUrl);
      }
    },
    [user]
  );

  return (
    <AssessmentContext.Provider
      value={useMemo(
        () => ({
          assessments,
          grade: studentGrade,
          subject,
          setSubject,
          subjects,
          licensed,
          loading,
          launchDiagnostic,
          launchScreener,
          reloadData
        }),
        [
          assessments,
          studentGrade,
          subject,
          subjects,
          setSubject,
          licensed,
          loading,
          launchDiagnostic,
          launchScreener,
          reloadData
        ]
      )}
    >
      {children}
    </AssessmentContext.Provider>
  );
}

export const useAssessments = () => useContext(AssessmentContext);
export type { IAssessmentContext };
